/**
 * Builds the PMS Scheduler board as an A4-portrait PDF — one table, grouped by
 * day with a shaded header row per day (its actual calendar date included, not
 * just "Day N"). jspdf + jspdf-autotable are dynamically imported so they stay
 * out of the main bundle.
 */
export interface ScheduleRow {
  name: string;
  group: string;
  location: string;
}
export interface ScheduleDayGroup {
  day: number | null; // null = unscheduled
  label: string; // "Day 3" or "Unscheduled"
  dateLabel: string; // "Tue, Sep 16, 2026" — empty for the unscheduled bucket
  today?: boolean;
  rows: ScheduleRow[];
}
export interface SchedulePdfInput {
  org?: { name?: string; subtitle?: string };
  logoDataUrl?: string;
  cycleLenDays: number;
  windowStartLabel: string;
  windowEndLabel: string;
  generatedAtLabel: string;
  totalItems: number;
  groups: ScheduleDayGroup[];
}

const NAME = 'U. N. Mehta Institute of Cardiology & Research Centre';
const SUB = 'Affiliated to B. J. Medical College, Ahmedabad-380016, INDIA';

export async function buildSchedulePdf(input: SchedulePdfInput): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 12;
  const org = input.org ?? {};

  if (input.logoDataUrl) {
    try { doc.addImage(input.logoDataUrl, 'PNG', M, M, 20, 13); } catch { /* bad image */ }
  }
  doc.setTextColor(18, 58, 134);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
  doc.text(org.name ?? NAME, pageW / 2, M + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  doc.text(`(${org.subtitle ?? SUB})`, pageW / 2, M + 10.5, { align: 'center' });
  doc.setDrawColor(18, 58, 134); doc.setLineWidth(0.5);
  doc.line(M, M + 14, pageW - M, M + 14);

  doc.setTextColor(17, 17, 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text('PMS Schedule', pageW / 2, M + 21, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(
    `${input.windowStartLabel} – ${input.windowEndLabel}  (${input.cycleLenDays}-day rotation)  ·  ${input.totalItems} items`,
    pageW / 2, M + 26.5, { align: 'center' },
  );
  doc.setFontSize(8); doc.setTextColor(110, 110, 110);
  doc.text(`Generated ${input.generatedAtLabel}`, pageW / 2, M + 31, { align: 'center' });

  type Cell = string | { content: string; colSpan?: number; styles?: Record<string, unknown> };
  const body: Cell[][] = [];
  input.groups.forEach((g) => {
    const count = g.rows.length;
    const heading = `${g.label}${g.dateLabel ? '  —  ' + g.dateLabel : ''}${g.today ? '   • TODAY' : ''}   (${count} item${count === 1 ? '' : 's'})`;
    const isUnsched = g.day === null;
    body.push([{
      content: heading,
      colSpan: 3,
      styles: {
        fontStyle: 'bold',
        halign: 'left',
        fillColor: isUnsched ? [253, 241, 227] : g.today ? [214, 226, 250] : [235, 239, 246],
        textColor: isUnsched ? [180, 83, 9] : [17, 17, 17],
        fontSize: 8.6,
      },
    }]);
    if (!count) {
      body.push([{ content: 'Nothing scheduled', colSpan: 3, styles: { fontStyle: 'italic', textColor: [140, 140, 140], halign: 'left' } }]);
    } else {
      g.rows.forEach((r) => body.push([r.name, r.group, r.location]));
    }
  });

  autoTable(doc, {
    head: [['Equipment', 'Type', 'Location']],
    body: body as never,
    startY: M + 36,
    margin: { left: M, right: M, bottom: 16 },
    tableLineColor: [210, 214, 222],
    tableLineWidth: 0.15,
    styles: { fontSize: 8.3, cellPadding: 2.1, lineColor: [210, 214, 222], lineWidth: 0.15, textColor: [17, 17, 17] },
    headStyles: { fillColor: [18, 58, 134], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8.6 },
    columnStyles: { 0: { cellWidth: 78 }, 1: { cellWidth: 38 }, 2: { cellWidth: 'auto' } },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(140, 140, 140);
      doc.text(`Page ${data.pageNumber}`, pageW - M, pageH - 8, { align: 'right' });
      doc.text(org.name ?? NAME, M, pageH - 8);
    },
  });

  return doc.output('blob');
}
